/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { AttributeDefinition, Component, RegionDefinition } from '@/lib/decorators';
import { DynamicImage } from '@/components/dynamic-image';
import type { Image } from '@/types';
import { cn } from '@/lib/utils';
import { FULL_IMAGE_CLASSES, getMarginBottomClass, isFullImage } from '@/lib/page-designer/style-attributes';

const DEFAULT_BACKGROUND_COLOR = '#000000';
const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const parseBackgroundColor = (value: string | undefined): string => {
    const trimmed = value?.trim();
    return trimmed && HEX_COLOR_REGEX.test(trimmed) ? trimmed : DEFAULT_BACKGROUND_COLOR;
};

/** Accept a standard Vimeo URL while refusing arbitrary iframe sources. */
const extractVimeoId = (value: string | undefined): string | null => {
    const trimmed = value?.trim();
    if (!trimmed) return null;

    const match = trimmed.match(/(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:video\/)?(\d+)/i);
    return match?.[1] ?? null;
};

/* v8 ignore start */
@Component('mediaMosaic', {
    name: 'Media Mosaic',
    description:
        'Four supporting images beside a featured Vimeo video or image. The featured media moves above the image grid on mobile.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class MediaMosaicMetadata {
    @AttributeDefinition({ id: 'image1', name: 'Supporting Image 1', type: 'image' })
    image1?: Image;

    @AttributeDefinition({ id: 'image1Alt', name: 'Supporting Image 1 Alt Text', type: 'string' })
    image1Alt?: string;

    @AttributeDefinition({ id: 'image2', name: 'Supporting Image 2', type: 'image' })
    image2?: Image;

    @AttributeDefinition({ id: 'image2Alt', name: 'Supporting Image 2 Alt Text', type: 'string' })
    image2Alt?: string;

    @AttributeDefinition({ id: 'image3', name: 'Supporting Image 3', type: 'image' })
    image3?: Image;

    @AttributeDefinition({ id: 'image3Alt', name: 'Supporting Image 3 Alt Text', type: 'string' })
    image3Alt?: string;

    @AttributeDefinition({ id: 'image4', name: 'Supporting Image 4', type: 'image' })
    image4?: Image;

    @AttributeDefinition({ id: 'image4Alt', name: 'Supporting Image 4 Alt Text', type: 'string' })
    image4Alt?: string;

    @AttributeDefinition({
        id: 'vimeoUrl',
        name: 'Featured Vimeo URL',
        description: 'Optional Vimeo URL. When populated, the video is shown instead of the featured image.',
        type: 'string',
    })
    vimeoUrl?: string;

    @AttributeDefinition({ id: 'featuredImage', name: 'Featured Image', type: 'image' })
    featuredImage?: Image;

    @AttributeDefinition({ id: 'featuredMediaAlt', name: 'Featured Media Alt Text', type: 'string' })
    featuredMediaAlt?: string;

    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Background Color',
        description: 'Hex color for the component background, e.g. #000000 or #000. Defaults to black.',
        type: 'string',
        defaultValue: DEFAULT_BACKGROUND_COLOR,
    })
    backgroundColor?: string;

    @AttributeDefinition({
        id: 'marginBottom',
        name: 'Style – Margin Bottom',
        type: 'enum',
        values: ['0', '2', '4', '6', '8', '12', '16'],
        defaultValue: '0',
        description: 'Space below the component (Tailwind spacing scale; 4 = 16px).',
    })
    marginBottom?: string;

    @AttributeDefinition({
        id: 'imageDisplay',
        name: 'Style – Image Display',
        type: 'enum',
        values: ['Crop to fit', 'Show full image'],
        defaultValue: 'Crop to fit',
        description: 'Show full image keeps the whole image at its natural height (no top/bottom crop).',
    })
    imageDisplay?: string;
}
/* v8 ignore stop */

interface MediaMosaicProps {
    image1?: Image;
    image1Alt?: string;
    image2?: Image;
    image2Alt?: string;
    image3?: Image;
    image3Alt?: string;
    image4?: Image;
    image4Alt?: string;
    vimeoUrl?: string;
    featuredImage?: Image;
    featuredMediaAlt?: string;
    backgroundColor?: string;
    marginBottom?: string;
    imageDisplay?: string;
}

function MosaicImage({ image, alt, fullImage }: { image?: Image; alt?: string; fullImage: boolean }) {
    return (
        <div className={cn('relative min-w-0 overflow-hidden bg-muted', !fullImage && 'aspect-square')}>
            {image?.url && (
                <DynamicImage
                    src={image.url}
                    alt={alt ?? ''}
                    widths={{ base: '50vw', lg: '25vw' }}
                    className={fullImage ? 'block w-full' : 'absolute inset-0 h-full w-full'}
                    imageProps={{ className: fullImage ? FULL_IMAGE_CLASSES : 'h-full w-full object-cover' }}
                />
            )}
        </div>
    );
}

export default function MediaMosaic({
    image1,
    image1Alt,
    image2,
    image2Alt,
    image3,
    image3Alt,
    image4,
    image4Alt,
    vimeoUrl,
    featuredImage,
    featuredMediaAlt,
    backgroundColor,
    marginBottom,
    imageDisplay,
}: MediaMosaicProps) {
    const vimeoId = extractVimeoId(vimeoUrl);
    const fullImage = isFullImage(imageDisplay);
    // Video needs a sized box, so the featured slot stays square when a Vimeo URL is set.
    const fullFeatured = fullImage && !vimeoId;
    const supportingImages = [
        { id: 'supporting-image-1', image: image1, alt: image1Alt },
        { id: 'supporting-image-2', image: image2, alt: image2Alt },
        { id: 'supporting-image-3', image: image3, alt: image3Alt },
        { id: 'supporting-image-4', image: image4, alt: image4Alt },
    ];

    return (
        <section
            className={cn('w-full px-6 py-10 lg:px-12 lg:py-12', getMarginBottomClass(marginBottom))}
            style={{ backgroundColor: parseBackgroundColor(backgroundColor) }}>
            <div className={cn('grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12', fullImage && 'items-start')}>
                <div
                    className={cn('order-2 grid grid-cols-2 gap-3 lg:order-1 lg:gap-12', fullImage && 'items-start')}
                    data-testid="supporting-grid">
                    {supportingImages.map(({ id, image, alt }) => (
                        <MosaicImage key={id} image={image} alt={alt} fullImage={fullImage} />
                    ))}
                </div>

                <div
                    className={cn(
                        'relative order-1 min-w-0 overflow-hidden bg-muted lg:order-2',
                        !fullFeatured && 'aspect-square'
                    )}
                    data-testid="featured-media">
                    {vimeoId ? (
                        <iframe
                            src={`https://player.vimeo.com/video/${vimeoId}?background=1&autoplay=1&muted=1&loop=1&autopause=0&playsinline=1&dnt=1`}
                            title={featuredMediaAlt || 'Featured video'}
                            allow="autoplay; fullscreen; picture-in-picture"
                            loading="lazy"
                            className="absolute inset-0 h-full w-full"
                            frameBorder={0}
                        />
                    ) : (
                        featuredImage?.url && (
                            <DynamicImage
                                src={featuredImage.url}
                                alt={featuredMediaAlt ?? ''}
                                widths={{ base: '100vw', lg: '50vw' }}
                                className={fullFeatured ? 'block w-full' : 'absolute inset-0 h-full w-full'}
                                imageProps={{
                                    className: fullFeatured ? FULL_IMAGE_CLASSES : 'h-full w-full object-cover',
                                }}
                            />
                        )
                    )}
                </div>
            </div>
        </section>
    );
}
